import metadata from "./CreateAccountFormComponent.metadata.json5";
import messages from "./CreateAccountFormComponent.messages";
import styles from "./CreateAccountFormComponent.module.scss";
import React, { useCallback, useContext, useState } from "react";
import { TranslatorContext } from "@jutro/locale";
import { ViewModelForm } from "gw-portals-viewmodel-react";
import { CustomIconUtil, CustomValidatorUtil } from "gw-portals-util-js";
import appConfig from 'app-config';
import { CustomCRMService } from "gw-capability-profileinfo";
import CustomCIAMService from "./services/CustomCIAMService";

function CreateAccountFormComponent(props) {
    const translator = useContext(TranslatorContext);
    const {currentStep, currentStepCallback} = props;
    const [accountType, setAccountType] = useState('')
    const [formData, updateFormData] = useState({});
    const [lastNameValidationMessages, setLastNameValidationMessages] = useState([]);
    const [firstNameValidationMessages, setFirstNameValidationMessages] = useState([]);
    const [businessNameValidationMessages, setBusinessNameValidationMessages] = useState([]);
    const [policyNumberValidationMessages, setPolicyNumberValidationMessages] = useState([]);
    const [zipCodeValidationMessages, setZipCodeValidationMessages] = useState([]);
    const [emailAddressValidationMessages, setEmailAddressValidationMessages] = useState([]);
    const [policySearchErrorMessage, setPolicySearchErrorMessage] = useState('')
    const [enterEmailErrorMessage, setEnterEmailErrorMessage] = useState('')
    
    const openPolicySearch = useCallback((acctType) => () => {
        setAccountType(acctType);
        currentStepCallback(acctType);    
    }, [accountType, currentStep]);
 
    const handlePolicySearchContinue = useCallback((curStep, nextStep) => async() => {
        const { policyNumber, zipCode, businessName, firstName, lastName } = formData;
        const verifyCall = await CustomCRMService.apiRequest(appConfig.env.CRM_BASE_URL+appConfig.env.CRM_VERIFY_POLICY,
                            'GET', null, {}, {}, { policyNumber, postalCode: zipCode, firstName, lastName, ...(!_.isEmpty(businessName) && {companyName: businessName})});
        if(verifyCall.canEnroll || verifyCall.canEnroll === 'true') {
            currentStepCallback(nextStep); 
        } else if(verifyCall.errorMessage === 'Policy not found.') {
            setPolicySearchErrorMessage(translator(messages.policyNotFoundError));
        } else if(verifyCall.errorMessage === 'Policy not serviceable.'
            || verifyCall.errorMessage === 'Leasing Desk Policy.') {
            setPolicySearchErrorMessage(translator(messages.policyNotServiceableError));
        } else if(verifyCall.errorMessage === 'Policy enrollment already exists.') {
            setPolicySearchErrorMessage(translator(messages.enrollmentAlreadyExistsError));
        } 
    }, [currentStep, formData, policySearchErrorMessage])

    const handleEnterEmailContinue = useCallback((curStep, nextStep) => async() => {
        const { policyNumber, zipCode, businessName, firstName, lastName, emailAddress } = formData;
        const verifyEmailCRM = await CustomCRMService.apiRequest(appConfig.env.CRM_BASE_URL+appConfig.env.CRM_VERIFY_USER,
                            'GET', null, {}, {}, {emailAddress});
        if(verifyEmailCRM.canEnroll || verifyEmailCRM.canEnroll === 'true') {
            currentStepCallback(nextStep); 
        } else if (verifyEmailCRM.errorMessage == 'Contact already exists for user email.') {
            setEnterEmailErrorMessage(translator(messages.emailAlreadyExistsError))
        }         
    }, [currentStep, formData])

    const handleBack = useCallback(( curStep, previousStep) => () => {
        currentStepCallback(previousStep);        
    }, [currentStep]);

    const handleEmailInviteButton = useCallback((curStep) => async () => {
        const { firstName, lastName, emailAddress } = formData;
        const access_token = await CustomCIAMService.getCIAMAccessToken();
        if (!_.isEmpty(access_token)) {
            const commandId = await CustomCIAMService.inviteUser(access_token, emailAddress, firstName, lastName);
            const userInvitationDto = await CustomCIAMService.validateInvitation(commandId, access_token);
            if (!_.isEmpty(userInvitationDto.invitationUrl)) {
                window.location.href = userInvitationDto.invitationUrl;
            }
        }
    }, [currentStep]);

    const isPolicySearchformValid = useCallback(() => {
        const { policyNumber, zipCode, businessName, firstName, lastName } = formData;

        if (hasValidationErrors(firstNameValidationMessages) 
            || hasValidationErrors(lastNameValidationMessages) 
            || hasValidationErrors(businessNameValidationMessages) 
            || hasValidationErrors(policyNumberValidationMessages) 
            || hasValidationErrors(zipCodeValidationMessages)) {
            return true;
        }

        if (accountType === 'individual') {
            if (_.isEmpty(policyNumber) 
                || _.isEmpty(zipCode) 
                || _.isEmpty(firstName) 
                || _.isEmpty(lastName)) {
                return true;
            }
        } else if (_.isEmpty(policyNumber) || _.isEmpty(zipCode) || _.isEmpty(businessName)) {
            return true;
        }

        return false;
    }, [formData, firstNameValidationMessages, lastNameValidationMessages, businessNameValidationMessages, policyNumberValidationMessages, zipCodeValidationMessages]);

    

    function hasValidationErrors(fieldValidationMessages) {
        if (!_.isEmpty(fieldValidationMessages) 
                && fieldValidationMessages[0] !== undefined 
                && fieldValidationMessages[0] !== null) {
            return true;
        }
        return false;
    }

    const validationCheck = useCallback((inputValue, inputValidator) => {
            const newValue = inputValue.currentTarget.value;
            switch (inputValidator.model) {
                case 'firstName':
                    setFirstNameValidationMessages([CustomValidatorUtil.nameValidation(newValue)]);
                    break;
                case 'lastName':
                    setLastNameValidationMessages([CustomValidatorUtil.nameValidation(newValue)]);
                    break;
                case 'businessName':
                    setBusinessNameValidationMessages([CustomValidatorUtil.businessNameValidation(newValue)]);
                    break;
                case 'policyNumber':
                    setPolicyNumberValidationMessages([CustomValidatorUtil.policyNumberValidation(newValue)]);
                    break;
                case 'zipCode':
                    setZipCodeValidationMessages([CustomValidatorUtil.zipCodeValidation(newValue)]);
                    break;
                case 'emailAddress':
                    setEmailAddressValidationMessages([CustomValidatorUtil.emailAddressValidation(newValue)]);
                    break;
                default:
                    break;
            }
        });  
   
  
    const overrideProps = {
        accountSummaryTextPara1: {
            content: translator(messages.accountSummaryTextPara1Text)
        },
        createAccountSummary: {
            visible: ['home', 'individual', 'business'].includes(currentStep),
            className: "create-account-summary"        
        },
        createAccountType: {
            visible: currentStep === 'home',
            className: "create-account-type"
        },
        individualImg: {
            src: CustomIconUtil.getLogoSrc("individual-user.svg")
        },
        individualImgHover: {
            src: CustomIconUtil.getLogoSrc("individual-user-hover.svg")
        },
        businessImg: {
            src: CustomIconUtil.getLogoSrc("business-user.svg")
        },
        businessImgHover: {
            src: CustomIconUtil.getLogoSrc("business-user-hover.svg")
        },
        createAccountTypeCardIndividual: {
            onClick: openPolicySearch('individual')
        },
        createAccountTypeCardBusiness: {
            onClick: openPolicySearch('business')
        },
        policySearchErrorMessageContainer: {
            visible: !_.isEmpty(policySearchErrorMessage)
        },
        policySearchErrorMessage: {
            message: policySearchErrorMessage,
        },
        individualPolicySearchformContainer: {
            visible: currentStep === 'individual'
        },
        businessPolicySearchformContainer: {
            visible: currentStep === 'business'
        },
        accountTypeLoginLink: {
            href: window.location.host
        },
        createAccountPolicySearchContainer: {
            visible: ['individual', 'business'].includes(currentStep), 
            className: "create-account-policy-search-container"
        },
        policySearchReturnLink: {
            onClick: handleBack(accountType, 'home')
        },
        policySearhButton: {
            content: translator(messages.continue),
            trigger:  handlePolicySearchContinue(accountType, 'enterMail'),
            disabled: isPolicySearchformValid()
        },       
        indPrimaryNamedInsuredLabel: {
            content: "Primary named insured"
        },
        indFirstName: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(firstNameValidationMessages),
            validationMessages: firstNameValidationMessages.filter((n) => n)
        },
         indLastName: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(lastNameValidationMessages),
            validationMessages: lastNameValidationMessages.filter((n) => n)
        },
        indpolicyNumber: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(policyNumberValidationMessages),
            validationMessages: policyNumberValidationMessages.filter((n) => n)
        },
        indZipCode: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(zipCodeValidationMessages),
            validationMessages: zipCodeValidationMessages.filter((n) => n)
        },
        busPrimaryNamedInsured: {
             onBlur: validationCheck,
            showErrors: hasValidationErrors(businessNameValidationMessages),
            validationMessages: businessNameValidationMessages.filter((n) => n)
        },
        busFirstName: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(firstNameValidationMessages),
            validationMessages: firstNameValidationMessages.filter((n) => n)
        },
        busLastName: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(lastNameValidationMessages),
            validationMessages: lastNameValidationMessages.filter((n) => n)
        },
        busPolicyNumber: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(policyNumberValidationMessages),
            validationMessages: policyNumberValidationMessages.filter((n) => n)
        },
         busZipCode: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(zipCodeValidationMessages),
            validationMessages: zipCodeValidationMessages.filter((n) => n)
        },
        termsOfUseLink: {
            content: "Terms of use.",
            href: appConfig.env.NOTIFICATION_TERMS_OF_USE_URL,
            target: '_blank'
        },
        createAccountConfirmMail: {
            visible: currentStep === 'enterMail',
            className: "create-account-email-invitation"
        },
        emailAddress: {
            onBlur: validationCheck,
            showErrors: hasValidationErrors(emailAddressValidationMessages),
            validationMessages: emailAddressValidationMessages.filter((n) => n)
        },
        createAccountConfirmMailErrorMessageContainer: {
            visible: !_.isEmpty(enterEmailErrorMessage)
        },
        createAccountConfirmMailErrorMessage: {
            message: enterEmailErrorMessage
        },
        accountEnterEmailBackAction: {
            onClick: handleBack(accountType, accountType)
        },
        accountEnterEmailContinueAction: {
            content: translator(messages.continue),
            trigger:  handleEnterEmailContinue(accountType, 'inviteMail'),
            disabled: (_.isEmpty(formData.emailAddress) || hasValidationErrors(emailAddressValidationMessages))
        },
        showemailaddressfieldPara: {
            content: translator(messages.showemailaddressfieldPara)
        },
        showemailaddressfieldParaName: {
            content: `${formData.firstName} ${formData.lastName}`
        },
        createAccountEmailInvitation: {
            visible: currentStep === 'inviteMail',
            className: "create-account-email-invitation"
        },
        showconfirmemailaddressPara1: {
            content: translator(messages.showconfirmemailaddressPara1)            
        },
        showconfirmemailaddressPara2: {
            content: formData.emailAddress
        },
        showconfirmemailaddressPara3: {
            content: translator(messages.showconfirmemailaddressPara3)            
        },
        changeItHereLink: {
            onClick: handleBack(accountType, 'enterMail')
        },
        accountEmailVerBackAction: {
            onClick: handleBack(accountType, 'enterMail')
        },
        accountEmailVercontinueAction: {
            onClick: handleEmailInviteButton(accountType)

        }
        
    };
    
    const resolvers = {
        resolveClassNameMap: styles,
    };
    
    const writeValue = useCallback(
        (value, path) => updateFormData({
            ...formData,
            [path]: value
        }),
        [formData]
    );
    
    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={formData}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}/>
    );   

}

export default CreateAccountFormComponent;